import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { authConfigured } from '../auth/config';
import AuthPanel from '../components/AuthPanel';
import { Button } from '../ui/button';
import { ErrorMessage, Field, Label } from '../ui/fieldset';
import { Input } from '../ui/input';
import { Text } from '../ui/text';

type Step = 'signup' | 'confirm';

export default function Signup() {
  const { signUp, confirmSignUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const loginHref = from ? `/login?from=${encodeURIComponent(from)}` : '/login';

  const [step, setStep] = useState<Step>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  if (!authConfigured) {
    return (
      <div className="mx-auto max-w-md py-10 text-center">
        <Text>Auth is not configured in this build.</Text>
      </div>
    );
  }

  const submitSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      const res = await signUp(email, password);
      if (res.needsConfirmation) setStep('confirm');
      else navigate(loginHref, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const submitConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      await confirmSignUp(email, code);
      navigate(loginHref, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (step === 'confirm') {
    return (
      <AuthPanel
        eyebrow="Email confirmation"
        title="Confirm your email"
        body={`We sent a 6-digit code to ${email}. Enter it here to finish your account.`}
      >
        <form onSubmit={submitConfirm} className="max-w-sm space-y-5">
          <Field>
            <Label>Confirmation code</Label>
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              minLength={6}
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
            />
          </Field>
          {error ? <ErrorMessage>{error}</ErrorMessage> : null}
          <Button type="submit" disabled={busy} color="dark/zinc" className="w-full">
            {busy ? 'Confirming…' : 'Confirm'}
          </Button>
        </form>
      </AuthPanel>
    );
  }

  return (
    <AuthPanel
      eyebrow="Fan access"
      title="Create your BaseballSTRM account"
      body="Create an account, confirm your email, then request access to the team you follow."
    >
      <form onSubmit={submitSignUp} className="max-w-sm space-y-5">
        <Field>
          <Label>Email</Label>
          <Input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field>
          <Label>Password</Label>
          <Input
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Text className="mt-1 text-xs">10+ chars, upper/lower/number.</Text>
        </Field>
        {error ? <ErrorMessage>{error}</ErrorMessage> : null}
        <Button type="submit" disabled={busy} color="dark/zinc" className="w-full">
          {busy ? 'Creating…' : 'Create account'}
        </Button>
        <Text className="text-center text-sm">
          Already have an account?{' '}
          <Link to={loginHref} className="font-medium text-zinc-200 hover:underline">
            Sign in
          </Link>
        </Text>
      </form>
    </AuthPanel>
  );
}
